import React, { forwardRef, createContext, HTMLAttributes, useState, useContext, useEffect } from 'react';
import classNames from 'classnames';
import { menu_base_css, menu_list_gap_css } from './styles';

export interface MenuContextProps {
  activeMenu?: string;
  activeMenuGroup?: string;
  setActiveMenu: (id?: string) => void;
  setActiveMenuGroup: (id?: string) => void;
  openMenuGroupIds: string[];
  /** MenuGroup 열림 상태를 토글합니다. accordion이면 나머지 그룹은 닫힙니다. */
  toggleMenuGroup: (groupId: string) => void;
  /** MenuGroup을 펼칩니다. 이미 열려 있으면 유지합니다. */
  expandMenuGroup: (groupId: string) => void;
  /** @deprecated openMenuGroupIds를 대신 사용해주세요. */
  openMenuGroup?: string;
  /** @deprecated toggleMenuGroup, expandMenuGroup을 대신 사용해주세요. */
  setOpenMenuGroup: (groupId?: string) => void;
  onClickMenu: ({ id, groupId }: { id?: string; groupId?: string }) => void;
  onMenuGroupOpenChange?: (groupId: string) => void;
  /** 한 번에 하나의 MenuGroup만 열립니다. */
  accordion: boolean;
  /** accordion일 때만 적용됩니다. */
  toggleMode: boolean;
  dense: boolean;
}

export const MenuContext = createContext<MenuContextProps | null>(null);

export const useMenuContext = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('MenuContext is not defined');
  }
  return context;
};

export interface MenuProps extends Omit<HTMLAttributes<HTMLUListElement>, 'onChange'> {
  /** 선택된 메뉴 ID */
  selected?: MenuContextProps['activeMenu'];
  /** 메뉴 선택 시 호출되는 함수 */
  onChange?: (id: MenuContextProps['activeMenu']) => void;
  /**
   * MenuGroup 서브 메뉴 open/close시 이벤트를 전달합니다.
   */
  onGroupOpenChange?: (id: MenuContextProps['activeMenuGroup']) => void;
  /**
   * 한 번에 하나의 MenuGroup만 열리게 합니다.
   * @default false
   */
  accordion?: MenuContextProps['accordion'];
  /**
   * 열려 있는 MenuGroup을 다시 눌러 닫을 수 있는지 여부입니다.
   * accordion일 때만 적용되며, 기본 동작(다중 열림)에서는 항상 토글되므로 무시됩니다.
   * @default false
   */
  collapsible?: MenuContextProps['toggleMode'];
  /** @default true */
  dense?: boolean;
}

export const Menu = forwardRef<HTMLUListElement, MenuProps>((menuProps, ref) => {
  const {
    className,
    children,
    selected,
    onChange,
    onGroupOpenChange,
    accordion = false,
    collapsible = false,
    dense = true,
    ...props
  } = menuProps;
  // selected를 넘기면 선택 상태를 소비처가 소유한다. 새 탭 링크처럼 화면이 그대로인 항목을 눌러도 표시가 옮겨가지 않는다.
  // selected={undefined}는 "선택 없음"이므로 값이 아니라 prop 전달 여부로 판별한다.
  const is_controlled = 'selected' in menuProps;
  const [uncontrolledActiveMenu, setUncontrolledActiveMenu] = useState<string | undefined>(selected);
  const activeMenu = is_controlled ? selected : uncontrolledActiveMenu;
  const setActiveMenu = (id?: string) => {
    if (!is_controlled) {
      setUncontrolledActiveMenu(id);
    }
  };
  const [activeMenuGroup, setActiveMenuGroup] = useState<string | undefined>();
  const [openMenuGroupIds, setOpenMenuGroupIds] = useState<string[]>([]);

  const expandMenuGroup: MenuContextProps['expandMenuGroup'] = (groupId) => {
    setOpenMenuGroupIds((prev) => {
      if (accordion) {
        return prev.length === 1 && prev[0] === groupId ? prev : [groupId];
      }
      return prev.includes(groupId) ? prev : [...prev, groupId];
    });
  };

  const toggleMenuGroup: MenuContextProps['toggleMenuGroup'] = (groupId) => {
    setOpenMenuGroupIds((prev) => {
      const is_opened = prev.includes(groupId);
      if (accordion) {
        if (!is_opened) {
          return [groupId];
        }
        return collapsible ? [] : prev;
      }
      return is_opened ? prev.filter((id) => id !== groupId) : [...prev, groupId];
    });
  };

  const setOpenMenuGroup: MenuContextProps['setOpenMenuGroup'] = (groupId) => {
    setOpenMenuGroupIds(groupId == null ? [] : [groupId]);
  };

  const handleClickMenu: MenuContextProps['onClickMenu'] = ({ id, groupId }) => {
    setActiveMenu(id);
    if (!is_controlled) {
      setActiveMenuGroup(groupId ?? undefined);
    }
    if (groupId != null) {
      expandMenuGroup(groupId);
    } else if (accordion) {
      setOpenMenuGroupIds([]);
    }
    onChange?.(id);
  };

  useEffect(() => {
    if (selected !== activeMenu) {
      setActiveMenu(selected);
      setActiveMenuGroup(undefined);
      // 다중 열림에서는 사용자가 펼쳐둔 그룹을 유지한다. 선택 항목이 속한 그룹은 MenuGroup이 스스로 펼친다.
      if (accordion) {
        setOpenMenuGroupIds([]);
      }
    }
  }, [selected]);

  return (
    <MenuContext.Provider
      value={{
        activeMenu,
        setActiveMenu,
        activeMenuGroup,
        setActiveMenuGroup,
        openMenuGroupIds,
        toggleMenuGroup,
        expandMenuGroup,
        openMenuGroup: openMenuGroupIds[0],
        setOpenMenuGroup,
        onClickMenu: handleClickMenu,
        onMenuGroupOpenChange: onGroupOpenChange,
        accordion,
        toggleMode: collapsible,
        dense,
      }}
    >
      <ul
        role='menu'
        className={classNames('pds-menu', className)}
        css={[menu_base_css, menu_list_gap_css]}
        {...props}
        ref={ref}
      >
        {children}
      </ul>
    </MenuContext.Provider>
  );
});
